import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FiArrowLeft, FiMessageSquare, FiMail, FiPhone, FiBriefcase } from 'react-icons/fi';
import { getDemande, getDemandeMessages, updateDemandeStatus } from '../utils/api';
import './DemandeDetail.css';

const DemandeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demande, setDemande] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000); // Rafraîchir toutes les 10 secondes
    return () => clearInterval(interval);
  }, [id]);

  const loadData = async () => {
    try {
      const [demandeRes, messagesRes] = await Promise.all([
        getDemande(id),
        getDemandeMessages(id)
      ]);
      setDemande(demandeRes.data);
      setMessages(messagesRes.data);
      setLoading(false);
      // Scroller vers le bas après le chargement
      setTimeout(() => {
        const container = document.querySelector('.messages-list');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDemandeStatus(id, newStatus);
      setDemande({ ...demande, status: newStatus });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!demande) {
    return <div className="error">Demande non trouvée</div>;
  }

  const getStatusBadge = (status) => {
    const badges = {
      en_attente: { label: 'En Attente', class: 'badge-warning' },
      en_cours: { label: 'En Cours', class: 'badge-info' },
      termine: { label: 'Terminé', class: 'badge-success' },
    };
    return badges[status] || { label: status, class: 'badge-default' };
  };

  const statusBadge = getStatusBadge(demande.status);

  return (
    <div className="demande-detail">
      <button className="back-button" onClick={() => navigate('/demandes')}>
        <FiArrowLeft /> Retour
      </button>

      <div className="detail-header">
        <div>
          <h2>{demande.projet}</h2>
          <span className={`badge ${statusBadge.class}`}>
            {statusBadge.label}
          </span>
        </div>
        <select
          className="status-select"
          value={demande.status}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          <option value="en_attente">En Attente</option>
          <option value="en_cours">En Cours</option>
          <option value="termine">Terminé</option>
        </select>
      </div>

      <div className="detail-content">
        <div className="detail-sidebar">
          <div className="info-card">
            <h3>Informations Client</h3>
            <div className="client-details">
              <div className="client-avatar-large">
                {demande.prenom[0]}{demande.nom[0]}
              </div>
              <h4>{demande.prenom} {demande.nom}</h4>
              {demande.entreprise && (
                <p className="entreprise">
                  <FiBriefcase /> {demande.entreprise}
                </p>
              )}
              <p className="email">
                <FiMail /> {demande.email}
              </p>
              {demande.telephone && (
                <p className="phone">
                  <FiPhone /> {demande.telephone}
                </p>
              )}
            </div>
          </div>

          <div className="info-card">
            <h3>Détails du Projet</h3>
            <div className="project-details">
              <div className="detail-item">
                <span className="label">Budget:</span>
                <span className="value">{demande.budget}</span>
              </div>
              <div className="detail-item">
                <span className="label">Délai souhaité:</span>
                <span className="value">{demande.delai}</span>
              </div>
              <div className="detail-item">
                <span className="label">Date de création:</span>
                <span className="value">
                  {format(new Date(demande.created_at), 'dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="detail-main">
          <div className="description-card">
            <h3>Description du Projet</h3>
            <p>{demande.description}</p>
          </div>

          <div className="messages-card">
            <div className="messages-header">
              <h3>
                <FiMessageSquare /> Messages ({messages.length})
              </h3>
            </div>
            <div className="messages-list">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.type === 'client' ? 'message-client' : 'message-employe'}`}
                >
                  <div className="message-header">
                    <span className="message-author">
                      {message.type === 'client'
                        ? `${message.client_prenom} ${message.client_nom}`
                        : `${message.employe_prenom} ${message.employe_nom}`}
                    </span>
                    <span className="message-time">
                      {format(new Date(message.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </span>
                  </div>
                  <div className="message-content">{message.contenu}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemandeDetail;

